import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button,
  IconButton, Typography, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions,
  MenuItem, Chip, Switch, FormControlLabel,
  Card, CardContent, Grid, CircularProgress,
  Avatar, FormControl, InputLabel, Select,
  InputAdornment, Tab, Tabs, Alert
} from '@mui/material';
import {
  Edit, Delete, Add, LocalCafe,
  Visibility, VisibilityOff, Search,
  LocalOffer, AccessTime, Inventory,
  Image as ImageIcon, Liquor, FreeBreakfast,
  Warning, Save
} from '@mui/icons-material';
import { boissonAPI, categorieBoissonAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';

const BoissonsPage = () => {
  const [boissons, setBoissons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    categorie: '',
    type_boisson: 'simple',
    prix: 0,
    produit_stock: '',
    temps_preparation: 5,
    est_disponible: true,
    image: null,
    imagePreview: null
  });

  const typeOptions = [
    { value: 'simple', label: 'Simple' },
    { value: 'cocktail', label: 'Cocktail' },
  ];

  // Fonction utilitaire pour extraire un tableau de la réponse API
  const extractArrayFromResponse = (data) => {
    if (Array.isArray(data)) {
      return data;
    } else if (data && typeof data === 'object') {
      const possibleArrayProps = ['results', 'data', 'items', 'boissons', 'categories'];
      for (const prop of possibleArrayProps) {
        if (Array.isArray(data[prop])) {
          return data[prop];
        }
      }
    }
    return [];
  };

  const fetchBoissons = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await boissonAPI.getAll();
      console.log('API Boissons Response:', response);
      console.log('Response data:', response.data);
      
      const boissonsArray = extractArrayFromResponse(response.data);
      setBoissons(boissonsArray);
      
      if (!Array.isArray(boissonsArray)) {
        console.warn('API boissons ne retourne pas un tableau:', response.data);
        setError('Format de données inattendu pour les boissons');
      }
    } catch (error) {
      console.error('Erreur détaillée:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          'Erreur lors du chargement des boissons';
      setError(errorMessage);
      toast.error(errorMessage);
      setBoissons([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categorieBoissonAPI.getAll();
      console.log('API Catégories Response:', response);
      console.log('Response data:', response.data);
      
      const categoriesArray = extractArrayFromResponse(response.data);
      if (Array.isArray(categoriesArray)) {
        setCategories(categoriesArray.filter(c => c.est_active));
      } else {
        console.warn('API catégories ne retourne pas un tableau:', response.data);
        setCategories([]);
      }
    } catch (error) {
      console.error('Erreur détaillée:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message ||
                          'Erreur lors du chargement des catégories';
      toast.error(errorMessage);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchBoissons();
    fetchCategories();
  }, []);

  const resetForm = () => {
    setFormData({
      nom: '',
      description: '',
      categorie: '',
      type_boisson: 'simple',
      prix: 0,
      produit_stock: '',
      temps_preparation: 5,
      est_disponible: true,
      image: null,
      imagePreview: null
    });
    setIsEditing(false);
  };

  const handleOpenDialog = (boisson = null) => {
    if (boisson) {
      setFormData({
        ...boisson,
        imagePreview: boisson.image
      });
      setIsEditing(true);
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        image: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.nom.trim()) {
      toast.error('Le nom de la boisson est requis');
      return;
    }
    
    if (!formData.categorie) {
      toast.error('La catégorie est requise');
      return;
    }
    
    if (!formData.prix || formData.prix <= 0) {
      toast.error('Le prix doit être supérieur à 0');
      return;
    }
    
    setSubmitting(true);
    
    // Arrondir le prix à 2 décimales
    const prixArrondi = Math.round(formData.prix * 100) / 100;
    const dataAEnvoyer = {
      ...formData,
      prix: prixArrondi
    };
    
    try {
      if (isEditing) {
        await boissonAPI.update(formData.id, dataAEnvoyer);
        toast.success('Boisson modifiée avec succès');
      } else {
        await boissonAPI.create(dataAEnvoyer);
        toast.success('Boisson créée avec succès');
      }
      
      setOpenDialog(false);
      resetForm();
      fetchBoissons();
    } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data?.nom?.[0] || 
                          error.response?.data?.detail || 
                          error.message ||
                          'Erreur lors de l\'enregistrement';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette boisson ?')) {
      try {
        await boissonAPI.delete(id);
        toast.success('Boisson supprimée avec succès');
        fetchBoissons();
      } catch (error) {
        console.error('Erreur:', error);
        const errorMessage = error.response?.data?.detail || 
                            error.message ||
                            'Erreur lors de la suppression';
        toast.error(errorMessage);
      }
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await boissonAPI.update(id, { est_disponible: !currentStatus });
      toast.success(`Boisson ${!currentStatus ? 'activée' : 'désactivée'}`);
      fetchBoissons();
    } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message ||
                          'Erreur lors de la modification du statut';
      toast.error(errorMessage);
    }
  };

  // Fonctions utilitaires pour garantir que nous travaillons avec des tableaux
  const getBoissonsArray = () => {
    if (!Array.isArray(boissons)) {
      console.warn('boissons n\'est pas un tableau:', boissons);
      return [];
    }
    return boissons;
  };

  const getCategoriesArray = () => {
    if (!Array.isArray(categories)) {
      console.warn('categories n\'est pas un tableau:', categories);
      return [];
    }
    return categories;
  };

  const boissonsArray = getBoissonsArray();
  const categoriesArray = getCategoriesArray();

  const filteredBoissons = boissonsArray.filter(boisson => {
    const matchesSearch = boisson.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         boisson.code?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    const matchesCategory = selectedCategory === 'all' || boisson.categorie === parseInt(selectedCategory);
    const matchesType = selectedType === 'all' || boisson.type_boisson === selectedType;
    const matchesStatus = activeTab === 0 ? boisson.est_disponible : !boisson.est_disponible;
    
    return matchesSearch && matchesCategory && matchesType && matchesStatus;
  });

  const getCategoryName = (categorieId) => {
    const categorie = categoriesArray.find(c => c.id === categorieId);
    return categorie ? categorie.nom : 'Inconnue';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'cocktail': return <Liquor fontSize="small" />;
      case 'simple': return <LocalCafe fontSize="small" />;
      default: return <FreeBreakfast fontSize="small" />;
    }
  };

  const totalRevenue = boissonsArray.reduce((sum, boisson) => 
    sum + (boisson.prix * (boisson.nombre_ventes || 0)), 0);

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
        p={3}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert 
          severity="error" 
          icon={<Warning />}
          sx={{ borderRadius: 2 }}
          action={
            <Button 
              variant="outlined" 
              size="small" 
              onClick={fetchBoissons}
            >
              Réessayer
            </Button>
          }
        >
          <Typography variant="h6" gutterBottom>
            Erreur de chargement
          </Typography>
          <Typography>
            {error}
          </Typography>
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              Détails de debug:
              <br />
              Type de boissons: {typeof boissons}
              <br />
              Est un tableau: {Array.isArray(boissons) ? 'Oui' : 'Non'}
            </Typography>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        p: 3,
        backgroundColor: 'background.default',
        minHeight: '100vh',
      }}
    >
      {/* Header avec statistiques */}
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <LocalCafe sx={{ mr: 2, verticalAlign: 'middle' }} />
              Boissons
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Gérez votre carte des boissons
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <TextField
              placeholder="Rechercher une boisson..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                sx: { borderRadius: 1 }
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ 
                height: 40,
                px: 3,
                borderRadius: 1
              }}
            >
              Nouvelle Boisson
            </Button>
          </Grid>
        </Grid>

        {/* Cartes statistiques */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Total Boissons
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {boissonsArray.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Disponibles
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {boissonsArray.filter(b => b.est_disponible).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Chiffre d'Affaires
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {totalRevenue.toLocaleString('fr-FR')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Ventes Total
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {boissonsArray.reduce((sum, boisson) => sum + (boisson.nombre_ventes || 0), 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Filtres et Tabs */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Catégorie</InputLabel>
            <Select
              value={selectedCategory}
              label="Catégorie"
              onChange={(e) => setSelectedCategory(e.target.value)}
              sx={{ borderRadius: 1 }}
            >
              <MenuItem value="all">Toutes les catégories</MenuItem>
              {categoriesArray.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={selectedType}
              label="Type"
              onChange={(e) => setSelectedType(e.target.value)}
              sx={{ borderRadius: 1 }}
            >
              <MenuItem value="all">Tous les types</MenuItem>
              {typeOptions.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ 
            '& .MuiTab-root': { minHeight: 48 }
          }}
        >
          <Tab 
            icon={<Visibility />} 
            label="Disponibles" 
            iconPosition="start"
          />
          <Tab 
            icon={<VisibilityOff />} 
            label="Indisponibles" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tableau des boissons */}
      <Paper 
        elevation={2} 
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Nom</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Catégorie</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Prix</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Stock</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Ventes</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBoissons.length > 0 ? (
                filteredBoissons.map((boisson) => (
                  <TableRow key={boisson.id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ py: 2 }}>
                      <Avatar
                        src={boisson.image}
                        variant="rounded"
                        sx={{ 
                          width: 60, 
                          height: 60,
                          borderRadius: 1,
                          backgroundColor: 'action.hover'
                        }}
                      >
                        {!boisson.image && <LocalCafe />}
                      </Avatar>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        {boisson.code}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography fontWeight="medium">{boisson.nom}</Typography>
                      <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                        {boisson.description || 'Aucune description'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={getCategoryName(boisson.categorie)}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getTypeIcon(boisson.type_boisson)}
                        <Typography variant="body2">
                          {typeOptions.find(t => t.value === boisson.type_boisson)?.label}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box display="flex" alignItems="center">
                        <LocalOffer fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography fontWeight="bold">
                          {parseFloat(boisson.prix).toFixed(2)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Inventory fontSize="small" />
                        <Typography color={boisson.stock_disponible > 0 ? 'success.main' : 'error.main'}>
                          {boisson.type_boisson === 'simple' ? 
                            (boisson.stock_disponible || 0) : 
                            'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography>
                        {boisson.nombre_ventes || 0}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={boisson.est_disponible}
                            onChange={() => toggleStatus(boisson.id, boisson.est_disponible)}
                            size="small"
                          />
                        }
                        label={
                          <Chip
                            label={boisson.est_disponible ? 'Disponible' : 'Indisponible'}
                            size="small"
                            color={boisson.est_disponible ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ borderRadius: 1 }}
                          />
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }} align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenDialog(boisson)}
                          size="small"
                          sx={{ 
                            '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(boisson.id)}
                          size="small"
                          sx={{ 
                            '&:hover': { backgroundColor: 'error.light', color: 'white' }
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <Typography variant="body1" color="textSecondary">
                        {searchTerm || selectedCategory !== 'all' || selectedType !== 'all'
                          ? 'Aucune boisson ne correspond aux critères de recherche'
                          : 'Aucune boisson trouvée'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Créez votre première boisson pour commencer
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog d'édition/création */}
      <Dialog 
        open={openDialog} 
        onClose={() => !submitting && setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <LocalCafe />
            <Typography variant="h6">
              {isEditing ? 'Modifier la Boisson' : 'Nouvelle Boisson'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Nom de la boisson"
                  fullWidth
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  required
                  error={!formData.nom.trim()}
                  helperText={!formData.nom.trim() ? 'Ce champ est requis' : ''}
                  disabled={submitting}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    value={formData.categorie}
                    label="Catégorie"
                    onChange={(e) => setFormData({...formData, categorie: e.target.value})}
                    disabled={submitting}
                    sx={{ borderRadius: 1 }}
                  >
                    {categoriesArray.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type de boisson</InputLabel>
                  <Select
                    value={formData.type_boisson}
                    label="Type de boisson"
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        type_boisson: e.target.value,
                        produit_stock: e.target.value === 'cocktail' ? '' : formData.produit_stock
                      });
                    }}
                    disabled={submitting}
                    sx={{ borderRadius: 1 }}
                  >
                    {typeOptions.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Prix"
                  type="number"
                  fullWidth
                  value={formData.prix}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setFormData({...formData, prix: ''});
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue)) {
                        setFormData({...formData, prix: numValue});
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end"></InputAdornment>,
                    inputProps: { 
                      step: "0.01",
                      min: "0"
                    },
                    sx: { borderRadius: 1 }
                  }}
                  required
                  error={!formData.prix || formData.prix <= 0}
                  helperText={!formData.prix || formData.prix <= 0 ? 'Le prix doit être supérieur à 0' : ''}
                  disabled={submitting}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  multiline
                  rows={2}
                  fullWidth
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Description de la boisson..."
                  disabled={submitting}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Image de la boisson
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ImageIcon />}
                      disabled={submitting}
                      sx={{ borderRadius: 1 }}
                    >
                      Choisir une image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={submitting}
                      />
                    </Button>
                    {formData.imagePreview && (
                      <Avatar
                        src={formData.imagePreview}
                        variant="rounded"
                        sx={{ 
                          width: 80, 
                          height: 80,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.est_disponible}
                      onChange={(e) => setFormData({...formData, est_disponible: e.target.checked})}
                      disabled={submitting}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Boisson disponible à la vente
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button 
            onClick={() => !submitting && setOpenDialog(false)} 
            variant="outlined"
            disabled={submitting}
            sx={{ borderRadius: 1 }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              submitting || 
              !formData.nom.trim() || 
              !formData.categorie || 
              !formData.prix || 
              formData.prix <= 0
            }
            startIcon={submitting ? <CircularProgress size={20} /> : (isEditing ? <Save /> : <Add />)}
            sx={{ borderRadius: 1 }}
          >
            {submitting ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoissonsPage;